import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box, Typography, Grid, Skeleton, Divider,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import Brightness2OutlinedIcon from "@mui/icons-material/Brightness2Outlined";
import WbTwilightIcon from "@mui/icons-material/WbTwilight";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useNavigate } from "react-router-dom";
import api from "../components/utils/api";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getGreeting() {
  const h = new Date().getHours();
  if (h >= 6  && h < 13) return { text: "Buenos días",   Icon: WbSunnyOutlinedIcon,      color: "#fbbf24" };
  if (h >= 13 && h < 20) return { text: "Buenas tardes", Icon: WbTwilightIcon,            color: "#ED9519" };
  return                        { text: "Buenas noches", Icon: Brightness2OutlinedIcon,   color: "#60a5fa" };
}

function getFormattedDate() {
  return new Date().toLocaleDateString("es-AR", {
    weekday: "long", day: "numeric", month: "long",
  });
}

function getTipoServicio(s) {
  if (s.tipoServicioReparacion === "1") return { label: "Reparación", color: "#ED9519" };
  if (s.tipoServicioServicio   === "1") return { label: "Servicio",   color: "#60a5fa" };
  if (s.tipoServicioGarantia  === "1") return { label: "Garantía",   color: "#a78bfa" };
  return { label: s.tipoServicioOtros || "—", color: "#9aa0b4" };
}

function getWeekRange() {
  const now  = new Date();
  const day  = now.getDay(); // 0=Dom, 1=Lun …
  const diff = day === 0 ? -6 : 1 - day; // días hasta el lunes
  const lunes = new Date(now);
  lunes.setDate(now.getDate() + diff);
  lunes.setHours(0, 0, 0, 0);
  const domingo = new Date(lunes);
  domingo.setDate(lunes.getDate() + 6);
  domingo.setHours(23, 59, 59, 999);
  return {
    desde: lunes.toISOString().split("T")[0],
    hasta: domingo.toISOString().split("T")[0],
  };
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const d = Math.floor(diff / 86400000);
  if (d === 0) return "hoy";
  if (d === 1) return "ayer";
  if (d < 7)   return `hace ${d} días`;
  return new Date(dateStr).toLocaleDateString("es-AR", { day: "numeric", month: "short" });
}

// ─── Sub-componente: KPI card ──────────────────────────────────────────────────
function KpiCard({ label, value, valueColor, loading }) {
  return (
    <Box sx={{
      bgcolor: "background.paper",
      border: "1px solid", borderColor: "divider",
      borderRadius: 2.5, p: "10px 14px",
    }}>
      {loading
        ? <Skeleton width={40} height={32} sx={{ mb: 0.3 }} />
        : <Typography sx={{ fontSize: 24, fontWeight: 700, color: valueColor || "text.primary", lineHeight: 1.1 }}>
            {value}
          </Typography>
      }
      <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── Sub-componente: Acceso rápido ────────────────────────────────────────────
function QuickAction({ icon: Icon, label, sublabel, iconBg, onClick }) {
  return (
    <Box onClick={onClick} sx={{
      bgcolor: "background.paper",
      border: "1px solid", borderColor: "divider",
      borderRadius: 2.5, p: "12px 14px",
      display: "flex", alignItems: "center", gap: 1.5,
      cursor: "pointer",
      transition: "background 0.15s",
      "&:active": { bgcolor: "rgba(237,149,25,0.06)" },
    }}>
      <Box sx={{
        width: 38, height: 38, borderRadius: 2,
        bgcolor: iconBg, display: "flex", alignItems: "center",
        justifyContent: "center", flexShrink: 0,
      }}>
        <Icon sx={{ fontSize: 20, color: "#ED9519" }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {sublabel}
        </Typography>
      </Box>
      <ArrowForwardIosIcon sx={{ fontSize: 11, color: "text.disabled", flexShrink: 0 }} />
    </Box>
  );
}

// ─── Sub-componente: Servicio reciente ────────────────────────────────────────
function RecentItem({ servicio }) {
  const tipo = getTipoServicio(servicio);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, py: 0.8 }}>
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: tipo.color, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: "text.primary",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {servicio.cliente}
        </Typography>
        <Typography variant="caption" sx={{ color: tipo.color }}>
          {tipo.label}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: "text.disabled", flexShrink: 0 }}>
        {timeAgo(servicio.fecha)}
      </Typography>
    </Box>
  );
}

// ─── Componente principal ──────────────────────────────────────────────────────
export default function Home() {
  const navigate   = useNavigate();
  const user       = localStorage.getItem("nombre-Completo") || "Técnico";
  const userToken  = localStorage.getItem("user-token");
  const greeting   = getGreeting();
  const GreetIcon  = greeting.Icon;

  const [servicios,    setServicios]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [horasData,    setHorasData]    = useState({ totalHoras: 0, registros: 0, porDia: Array(7).fill(0) });
  const [loadingHoras, setLoadingHoras] = useState(true);

  const offlinePendientes = useMemo(
    () => (JSON.parse(localStorage.getItem("offlinePosts")) || []).length,
    []
  );

  const fetchServicios = useCallback(async () => {
    try {
      const res = await fetch(api.urlAPI + "servicios", {
        headers: { "iron-token": userToken },
      });
      if (res.ok) setServicios(await res.json());
    } catch {
      // sin conexión — mostramos lo que haya
    } finally {
      setLoading(false);
    }
  }, [userToken]);

  useEffect(() => { fetchServicios(); }, [fetchServicios]);

  const fetchHoras = useCallback(async () => {
    try {
      const { desde, hasta } = getWeekRange();
      const params = new URLSearchParams({ desde, hasta });
      const res = await fetch(`${api.urlAPI}horas?${params}`, {
        headers: { "iron-token": userToken },
      });
      if (res.ok) {
        const raw = await res.json();
        // Siempre filtrar por el usuario logueado (admins ven todos en otras pantallas,
        // pero acá queremos solo las horas propias)
        const miNombre = localStorage.getItem("nombre-Completo") || "";
        const data = miNombre ? raw.filter(h => h.usuario === miNombre) : raw;
        const totalHoras = data.reduce((sum, h) => sum + (parseFloat(h.totalHoras) || 0), 0);
        // Agrupar por día de la semana (0=Lun … 6=Dom)
        const porDia = Array(7).fill(0);
        data.forEach(h => {
          if (!h.fecha) return;
          const dow = (new Date(h.fecha).getDay() + 6) % 7; // Sun(0)→6, Mon(1)→0
          porDia[dow] = Math.round((porDia[dow] + (parseFloat(h.totalHoras) || 0)) * 10) / 10;
        });
        setHorasData({ totalHoras: Math.round(totalHoras * 10) / 10, registros: data.length, porDia });
      }
    } catch {
      // sin conexión
    } finally {
      setLoadingHoras(false);
    }
  }, [userToken]);

  useEffect(() => { fetchHoras(); }, [fetchHoras]);

  // ─── KPIs ──────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total     = servicios.length;
    const enviados  = servicios.filter(s => s.flagCrm === "1").length;
    const pendientes= total - enviados;
    const now       = new Date();
    const esMes     = servicios.filter(s => {
      const d = new Date(s.fecha);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    return { total, enviados, pendientes, esMes };
  }, [servicios]);

  // ─── Últimos 3 servicios ───────────────────────────────────────────────────
  const recientes = useMemo(
    () => [...servicios].sort((a, b) => new Date(b.fecha) - new Date(a.fecha)).slice(0, 3),
    [servicios]
  );

  const primerNombre = user.split(" ")[0];
  const fecha        = getFormattedDate();

  return (
    <Box sx={{ p: 2, bgcolor: "background.default", minHeight: "calc(100vh - 56px)" }}>

      {/* ── Saludo ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: "50%",
          bgcolor: "rgba(237,149,25,0.12)",
          border: "1.5px solid rgba(237,149,25,0.25)",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <GreetIcon sx={{ fontSize: 24, color: greeting.color }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "capitalize" }}>
            {greeting.text}
          </Typography>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
            {primerNombre}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.disabled", textTransform: "capitalize" }}>
            {fecha}
          </Typography>
        </Box>
      </Box>

      {/* ── KPIs ── */}
      <Grid container spacing={1.2} sx={{ mb: 2.5 }}>
        {[
          { label: "Total",         value: kpis.total,     color: "text.primary"  },
          { label: "Enviados CRM",  value: kpis.enviados,  color: "#4ade80"       },
          { label: "Pendientes CRM",value: kpis.pendientes,color: "#fbbf24"       },
          { label: "Este mes",      value: kpis.esMes,     color: "#ED9519"       },
        ].map(({ label, value, color }) => (
          <Grid item xs={6} key={label}>
            <KpiCard label={label} value={value} valueColor={color} loading={loading} />
          </Grid>
        ))}
      </Grid>

      {/* ── Horas esta semana ── */}
      {(() => {
        const DAYS   = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
        const todayDow = (new Date().getDay() + 6) % 7; // 0=Lun … 6=Dom
        const maxVal = Math.max(...horasData.porDia, 1);
        return (
          <Box sx={{
            bgcolor: "background.paper",
            border: "1px solid", borderColor: "divider",
            borderRadius: 2.5, p: "12px 14px", mb: 2.5,
          }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: "rgba(237,149,25,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <AccessTimeIcon sx={{ fontSize: 18, color: "#ED9519" }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", lineHeight: 1.2 }}>
                  Horas esta semana
                </Typography>
                {loadingHoras ? (
                  <Skeleton width={80} height={22} />
                ) : (
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6 }}>
                    <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#ED9519", lineHeight: 1 }}>
                      {horasData.totalHoras}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      hs · {horasData.registros} {horasData.registros === 1 ? "registro" : "registros"}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Gráfico de barras por día */}
            {loadingHoras ? (
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", height: 56 }}>
                {DAYS.map(d => <Skeleton key={d} variant="rectangular" width="100%" height={`${Math.random()*60+20}%`} sx={{ borderRadius: 1 }} />)}
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: "6px", alignItems: "flex-end", height: 64 }}>
                {horasData.porDia.map((val, i) => {
                  const isToday   = i === todayDow;
                  const barHeight = maxVal > 0 ? Math.max((val / maxVal) * 100, val > 0 ? 8 : 3) : 3;
                  return (
                    <Box key={i} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                      {val > 0 && (
                        <Typography sx={{ fontSize: 9, color: isToday ? "#ED9519" : "text.disabled", fontWeight: isToday ? 700 : 400, lineHeight: 1 }}>
                          {val}
                        </Typography>
                      )}
                      <Box sx={{
                        width: "100%",
                        height: `${barHeight}%`,
                        minHeight: 3,
                        borderRadius: "3px 3px 1px 1px",
                        bgcolor: isToday
                          ? "#ED9519"
                          : val > 0
                            ? "rgba(237,149,25,0.35)"
                            : "rgba(255,255,255,0.06)",
                        transition: "height 0.3s ease",
                      }} />
                      <Typography sx={{ fontSize: 9, color: isToday ? "#ED9519" : "text.disabled", fontWeight: isToday ? 700 : 400, lineHeight: 1 }}>
                        {DAYS[i]}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })()}

      {/* ── Pendientes offline ── */}
      {offlinePendientes > 0 && (
        <Box onClick={() => navigate("/Servicios-Pendientes")} sx={{
          bgcolor: "rgba(251,191,36,0.07)", border: "1px solid rgba(251,191,36,0.2)",
          borderRadius: 2.5, p: "10px 14px", mb: 2.5,
          display: "flex", alignItems: "center", gap: 1.2, cursor: "pointer",
        }}>
          <PendingActionsIcon sx={{ fontSize: 18, color: "#fbbf24", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, color: "#fbbf24", flex: 1 }}>
            Tenés <strong>{offlinePendientes}</strong> {offlinePendientes === 1 ? "servicio guardado" : "servicios guardados"} offline sin enviar
          </Typography>
          <ArrowForwardIosIcon sx={{ fontSize: 10, color: "#fbbf24", opacity: 0.6 }} />
        </Box>
      )}

      {/* ── Accesos rápidos ── */}
      <Typography variant="caption" sx={{
        color: "text.disabled", textTransform: "uppercase",
        letterSpacing: "0.08em", display: "block", mb: 1,
      }}>
        Accesos rápidos
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2.5 }}>
        <QuickAction
          icon={AddCircleOutlineIcon}
          label="Nuevo servicio"
          sublabel="Crear solicitud de servicio técnico"
          iconBg="rgba(237,149,25,0.12)"
          onClick={() => navigate("/NuevoServicio")}
        />
        <QuickAction
          icon={AssignmentOutlinedIcon}
          label="Ver servicios"
          sublabel="Historial de servicios realizados"
          iconBg="rgba(96,165,250,0.1)"
          onClick={() => navigate("/Servicios")}
        />
        <QuickAction
          icon={TrendingUpIcon}
          label="Reportes"
          sublabel="Reportes de servicio e inspección"
          iconBg="rgba(167,139,250,0.1)"
          onClick={() => navigate("/ReporteServicio")}
        />
      </Box>

      {/* ── Últimos servicios ── */}
      <Typography variant="caption" sx={{
        color: "text.disabled", textTransform: "uppercase",
        letterSpacing: "0.08em", display: "block", mb: 1,
      }}>
        Últimos servicios
      </Typography>

      <Box sx={{
        bgcolor: "background.paper", border: "1px solid", borderColor: "divider",
        borderRadius: 2.5, px: 1.5, py: 0.5,
      }}>
        {loading
          ? [1, 2, 3].map(i => (
              <Box key={i} sx={{ py: 0.8, display: "flex", alignItems: "center", gap: 1.2 }}>
                <Skeleton variant="circular" width={7} height={7} />
                <Skeleton width="60%" height={16} />
                <Box sx={{ flex: 1 }} />
                <Skeleton width={24} height={12} />
              </Box>
            ))
          : recientes.length > 0
            ? recientes.map((s, i) => (
                <React.Fragment key={s.id}>
                  <RecentItem servicio={s} />
                  {i < recientes.length - 1 && <Divider />}
                </React.Fragment>
              ))
            : (
              <Typography variant="caption" sx={{ color: "text.disabled", py: 1.5, display: "block", textAlign: "center" }}>
                Sin servicios registrados aún
              </Typography>
            )
        }
      </Box>

    </Box>
  );
}
